import { useId } from 'react';
import type { LabelHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
  hint?: ReactNode;
}

export function Label({ className, children, required, hint, ...props }: LabelProps) {
  return (
    <label
      className={cn('flex items-center gap-1.5 text-sm font-medium text-fg', className)}
      {...props}
    >
      {children}
      {required && (
        <span className="text-danger" title="Required">
          *
        </span>
      )}
      {hint && <span className="text-xs font-normal text-subtle">{hint}</span>}
    </label>
  );
}

export interface FieldProps {
  label?: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  /** Right-aligned counter or action on the label row. */
  labelAction?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** Label + control + hint/error, the standard form row. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  labelAction,
  className,
  children,
}: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {(label || labelAction) && (
        <div className="flex items-center justify-between gap-3">
          {label ? (
            <Label htmlFor={htmlFor} required={required}>
              {label}
            </Label>
          ) : (
            <span />
          )}
          {labelAction}
        </div>
      )}
      {children}
      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs leading-relaxed text-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

/** Generates a stable id for a label/control pair when the caller has none. */
export function useFieldId(provided?: string): string {
  const generated = useId();
  return provided ?? generated;
}
